import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { PLAN_LIMITS } from "../lib/plans";
import { trackEvent } from "../lib/analytics";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Slider } from "../components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  ChevronLeft,
  ChevronRight,
  Sparkles,
  RefreshCw,
  Wand2,
  Lock,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  "Pick kid",
  "Storyline",
  "Avatar",
  "Style",
  "Pages",
  "Script",
  "Generate",
];

const ART_STYLES = [
  "Classic Comic",
  "Kawaii/Chibi",
  "Watercolor Storybook",
  "Retro Superhero",
  "Manga",
];
const FONTS = [
  "Bangers",
  "Comic Sans MS",
  "Anton",
  "Outfit",
  "Nunito",
  "Permanent Marker",
];
const COSTUMES = [
  "superhero",
  "superheroine",
  "explorer",
  "wizard",
  "pirate",
  "scientist",
  "knight",
];
const ACCESSORIES = [
  "cape",
  "hat",
  "gadget",
  "wings",
  "wand",
  "shield",
  "goggles",
];
const SIDEKICKS = ["puppy", "cat", "owl", "dragon", "robot", "bunny", "none"];
const HAIRS = [
  "short_brown",
  "long_blonde",
  "curly_black",
  "buzz_red",
  "ponytail_pink",
  "bald",
];
const SKIN_TONES = ["#F5C7A1", "#E0AC8A", "#C99070", "#8D5A3C", "#5C3A23"];

// Hair color map for live preview
const HAIR_COLORS = {
  short_brown: "#8B4513",
  long_blonde: "#FFD700",
  curly_black: "#1a1a1a",
  buzz_red: "#CC2200",
  ponytail_pink: "#FF69B4",
  bald: "transparent",
};

// Costume to emoji + label map
const COSTUME_META = {
  superhero: { emoji: "🦸", label: "Superhero", color: "#9D4CDD" },
  superheroine: { emoji: "🦸‍♀️", label: "Superheroine", color: "#EC4899" },
  explorer: { emoji: "🧭", label: "Explorer", color: "#10B981" },
  wizard: { emoji: "🧙", label: "Wizard", color: "#6366F1" },
  pirate: { emoji: "🏴‍☠️", label: "Pirate", color: "#374151" },
  scientist: { emoji: "🥽", label: "Scientist", color: "#3B82F6" },
  knight: { emoji: "🛡️", label: "Knight", color: "#6B7280" },
};

// Sidekick emoji map
const SIDEKICK_EMOJI = {
  puppy: "🐶",
  cat: "🐱",
  owl: "🦉",
  dragon: "🐉",
  robot: "🤖",
  bunny: "🐰",
  none: "",
};

// Accessory emoji map
const ACCESSORY_EMOJI = {
  cape: "🦸",
  hat: "🎩",
  gadget: "📱",
  wings: "🪽",
  wand: "🪄",
  shield: "🛡️",
  goggles: "🥽",
};

export default function CreateWizard() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const initialChildId = params.get("child");

  // Skip "Pick kid" step when the child is already known (kid login or URL param)
  const skipPickKid = !!(initialChildId || user?.role === "kid");
  const startStep = skipPickKid ? 1 : 0;

  const [step, setStep] = useState(startStep);
  const [profiles, setProfiles] = useState([]);
  const [storylines, setStorylines] = useState([]);
  const [comicId, setComicId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generatingScript, setGeneratingScript] = useState(false);

  const [data, setData] = useState({
    child_id:
      initialChildId || (user?.role === "kid" ? user.child_id : "") || "",
    title: "",
    storyline: "",
    custom_prompt: "",
    art_style: "Classic Comic",
    font: "Bangers",
    page_count: 6,
    panel_layout: "single",
    avatar: {
      name: "Hero",
      body_type: "average",
      skin_tone: "#F5C7A1",
      hair: "short_brown",
      eyes: "round_brown",
      expression: "happy",
      costume: "superhero",
      accessories: ["cape"],
      sidekick: "puppy",
    },
    panels: [],
  });

  useEffect(() => {
    api.get("/profiles").then((r) => setProfiles(r.data));
    api.get("/storylines").then((r) => setStorylines(r.data));
  }, []);

  // Plan limit gate
  const planKey = user?.plan || "free";
  const booksLimit = PLAN_LIMITS[planKey] ?? 0;
  const booksUsed = user?.books_used_this_month ?? 0;
  const bonusBooks = user?.bonus_books ?? 0;
  const totalAllowed = booksLimit + bonusBooks;
  const atLimit = booksUsed >= totalAllowed;

  // Block direct URL access when limit is reached
  if (atLimit) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-xl mx-auto px-6 py-20 text-center">
          <div className="cv-border cv-shadow-lg rounded-2xl bg-white p-10">
            <div className="text-6xl mb-4">🔒</div>
            <h2
              className="font-headings uppercase text-3xl mb-3"
              data-testid="limit-reached-title"
            >
              Monthly limit reached
            </h2>
            <p className="font-body font-bold text-black/70 mb-2">
              You've used{" "}
              <span className="text-purple-600 font-bold">
                {booksUsed} / {totalAllowed}
              </span>{" "}
              books this month.
            </p>
            <p className="font-body text-sm text-black/60 mb-8">
              {planKey === "free"
                ? "Subscribe to a plan to start creating comics for your kids."
                : "Upgrade your plan to create more comics this month, or wait until your quota resets."}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/pricing">
                <Button
                  className="bg-purple-600 hover:bg-purple-500 text-white cv-border cv-shadow cv-press font-headings uppercase rounded-xl px-8 py-4 text-lg w-full sm:w-auto"
                  data-testid="limit-upgrade-btn"
                >
                  {planKey === "free" ? "Pick a plan" : "Upgrade plan"}
                </Button>
              </Link>
              <Link to="/dashboard">
                <Button
                  variant="outline"
                  className="cv-border-3 font-headings uppercase rounded-xl px-8 py-4 text-lg w-full sm:w-auto"
                  data-testid="limit-back-btn"
                >
                  Back to dashboard
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const upd = (k, v) => setData((prev) => ({ ...prev, [k]: v }));
  const updAvatar = (k, v) =>
    setData((prev) => ({ ...prev, avatar: { ...prev.avatar, [k]: v } }));

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, startStep));

  const canNext = () => {
    if (step === 0) return !!data.child_id;
    if (step === 1)
      return !!data.title && (!!data.storyline || !!data.custom_prompt.trim());
    if (step === 5) return data.panels.length > 0;
    return true;
  };

  // Step 5: generate script via API
  const generateScript = async () => {
    setGeneratingScript(true);
    try {
      let cid = comicId;
      if (!cid) {
        const r = await api.post("/comics", {
          child_id: data.child_id,
          title: data.title,
          storyline: data.storyline || "custom",
          custom_prompt: data.custom_prompt,
          art_style: data.art_style,
          font: data.font,
          page_count: data.page_count,
          panel_layout: data.panel_layout,
          avatar: data.avatar,
        });
        cid = r.data.id;
        setComicId(cid);
      }
      const r = await api.post(`/comics/${cid}/script`);
      upd("panels", r.data.panels);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Script generation failed");
    } finally {
      setGeneratingScript(false);
    }
  };

  const editPanel = async (idx, field, value) => {
    const newPanels = [...data.panels];
    newPanels[idx] = { ...newPanels[idx], [field]: value };
    upd("panels", newPanels);
  };

  const savePanel = async (idx) => {
    const p = data.panels[idx];
    try {
      await api.patch(`/comics/${comicId}/panels/${idx}`, {
        dialogue: p.dialogue,
        sfx: p.sfx,
        narration: p.narration,
        scene_description: p.scene_description,
      });
      toast.success("Saved!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const rewritePanel = async (idx) => {
    try {
      const r = await api.post(`/comics/${comicId}/panels/${idx}/rewrite`);
      const newPanels = [...data.panels];
      newPanels[idx] = { ...newPanels[idx], ...r.data };
      upd("panels", newPanels);
      toast.success("Panel rewritten!");
    } catch (err) {
      toast.error("Rewrite failed");
    }
  };

  const startGenerate = async () => {
    setGenerating(true);
    try {
      // save all panels first
      for (let i = 0; i < data.panels.length; i++) {
        const p = data.panels[i];
        await api.patch(`/comics/${comicId}/panels/${i}`, {
          dialogue: p.dialogue,
          sfx: p.sfx,
          narration: p.narration,
          scene_description: p.scene_description,
        });
      }
      await api.post(`/comics/${comicId}/generate`);
      trackEvent("comic_created", {
        comic_id: comicId,
        art_style: data.art_style,
        page_count: data.page_count,
      });
      navigate(`/comic/${comicId}`);
    } catch (err) {
      const status = err?.response?.status;
      const message = err?.response?.data?.detail || "Generation failed";
      if (status === 403) {
        toast.error(message, {
          duration: 6000,
          action: {
            label: "Upgrade plan",
            onClick: () => navigate("/pricing"),
          },
        });
      } else {
        toast.error(message);
      }
      setGenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Comic-panel stepper — hide "Pick kid" step for kids */}
        <div
          className={`grid gap-2 mb-8 ${
            skipPickKid ? "grid-cols-6" : "grid-cols-7"
          }`}
        >
          {STEPS.map((s, i) => {
            if (skipPickKid && i === 0) return null; // hide "Pick kid" for kids
            return (
              <div
                key={i}
                className={`cv-border-3 rounded-xl p-2 text-center font-headings uppercase text-xs ${
                  i < step
                    ? "bg-purple-500 text-white"
                    : i === step
                      ? "bg-yellow-400 cv-shadow-sm"
                      : "bg-white"
                }`}
                data-testid={`wizard-step-${i}`}
              >
                <div className="text-base">{skipPickKid ? i : i + 1}</div>
                <div className="leading-tight hidden md:block">{s}</div>
              </div>
            );
          })}
        </div>

        <div className="cv-border cv-shadow-lg rounded-2xl bg-white p-6 md:p-10 min-h-[400px]">
          {step === 0 && (
            <div>
              <h2 className="font-headings uppercase text-3xl mb-4">
                Who's the hero today?
              </h2>
              {profiles.length === 0 ? (
                <p className="font-body font-bold">
                  Add a child profile first from the dashboard.
                </p>
              ) : (
                <div className="grid sm:grid-cols-3 gap-4">
                  {profiles.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => upd("child_id", p.id)}
                      className={`cv-border-3 rounded-2xl p-5 cv-press text-left ${
                        data.child_id === p.id
                          ? "bg-yellow-300 cv-shadow"
                          : "bg-white cv-shadow-sm"
                      }`}
                      data-testid={`pick-child-${p.id}`}
                    >
                      <div className="text-5xl mb-2">{p.avatar_emoji}</div>
                      <h3 className="font-headings uppercase text-xl">
                        {p.nickname}
                      </h3>
                      <p className="font-body font-bold text-sm">
                        Age {p.age} · {p.reading_level}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 1 && (
            <div>
              <h2 className="font-headings uppercase text-3xl mb-4">
                Pick a storyline
              </h2>
              <div className="mb-4">
                <Label className="font-headings uppercase">Comic Title</Label>
                <Input
                  className="cv-border-3 mt-1 rounded-xl font-body font-bold"
                  value={data.title}
                  onChange={(e) => upd("title", e.target.value)}
                  placeholder="The Adventures of..."
                  data-testid="comic-title"
                />
              </div>
              <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                {storylines.map((s) => (
                  <button
                    key={s.key}
                    onClick={() => upd("storyline", s.key)}
                    className={`cv-border-3 rounded-2xl p-4 cv-press text-left ${
                      data.storyline === s.key ? "cv-shadow" : "cv-shadow-sm"
                    }`}
                    style={{
                      background: data.storyline === s.key ? s.color : "#fff",
                    }}
                    data-testid={`storyline-${s.key}`}
                  >
                    <div className="text-4xl mb-2">{s.emoji}</div>
                    <h3 className="font-headings uppercase text-base leading-tight">
                      {s.title}
                    </h3>
                    <p
                      className={`font-body text-xs mt-1 ${
                        data.storyline === s.key
                          ? "text-white"
                          : "text-black/70"
                      }`}
                    >
                      {s.blurb}
                    </p>
                  </button>
                ))}
              </div>
              <div>
                <Label className="font-headings uppercase">
                  Or describe your own idea (optional)
                </Label>
                <Textarea
                  className="cv-border-3 mt-1 rounded-xl font-body font-bold"
                  rows={3}
                  value={data.custom_prompt}
                  onChange={(e) => upd("custom_prompt", e.target.value)}
                  placeholder="e.g., a friendly dinosaur who loves pizza and learns to share"
                  data-testid="custom-prompt"
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h2 className="font-headings uppercase text-3xl mb-4">
                Build your hero
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div>
                    <Label className="font-headings uppercase">Hero name</Label>
                    <Input
                      className="cv-border-3 mt-1 rounded-xl font-body font-bold"
                      value={data.avatar.name}
                      onChange={(e) => updAvatar("name", e.target.value)}
                      data-testid="avatar-name"
                    />
                  </div>
                  <div>
                    <Label className="font-headings uppercase">Costume</Label>
                    <Select
                      value={data.avatar.costume}
                      onValueChange={(v) => updAvatar("costume", v)}
                    >
                      <SelectTrigger
                        className="cv-border-3 mt-1 rounded-xl font-body font-bold"
                        data-testid="avatar-costume"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COSTUMES.map((c) => (
                          <SelectItem key={c} value={c}>
                            {c}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="font-headings uppercase">Hair</Label>
                    <Select
                      value={data.avatar.hair}
                      onValueChange={(v) => updAvatar("hair", v)}
                    >
                      <SelectTrigger className="cv-border-3 mt-1 rounded-xl font-body font-bold">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {HAIRS.map((h) => (
                          <SelectItem key={h} value={h}>
                            {h.replace("_", " ")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="font-headings uppercase mb-2 block">
                      Skin tone
                    </Label>
                    <div className="flex gap-2">
                      {SKIN_TONES.map((t) => (
                        <button
                          key={t}
                          onClick={() => updAvatar("skin_tone", t)}
                          className={`w-10 h-10 cv-border-3 rounded-full ${
                            data.avatar.skin_tone === t ? "cv-shadow-sm" : ""
                          }`}
                          style={{ background: t }}
                        />
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="font-headings uppercase">Sidekick</Label>
                    <Select
                      value={data.avatar.sidekick}
                      onValueChange={(v) => updAvatar("sidekick", v)}
                    >
                      <SelectTrigger className="cv-border-3 mt-1 rounded-xl font-body font-bold">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SIDEKICKS.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="font-headings uppercase mb-2 block">
                      Accessories
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {ACCESSORIES.map((a) => (
                        <button
                          key={a}
                          onClick={() => {
                            const has = data.avatar.accessories.includes(a);
                            updAvatar(
                              "accessories",
                              has
                                ? data.avatar.accessories.filter((x) => x !== a)
                                : [...data.avatar.accessories, a],
                            );
                          }}
                          className={`cv-border-3 px-3 py-1 rounded-lg font-headings uppercase text-xs ${
                            data.avatar.accessories.includes(a)
                              ? "bg-pink-400 text-white cv-shadow-sm"
                              : "bg-white"
                          }`}
                        >
                          {a}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                {/* Live avatar preview */}
                <div className="cv-border-3 rounded-2xl bg-yellow-50 p-6 text-center cv-shadow flex flex-col items-center justify-center min-h-[300px] gap-3">
                  {/* Head with skin tone */}
                  <div className="relative flex flex-col items-center">
                    {/* Body / costume */}
                    <div
                      className="w-16 h-12 rounded-b-xl cv-border-3 flex items-center justify-center text-3xl mt-[-2px]"
                      style={{
                        background:
                          COSTUME_META[data.avatar.costume]?.color || "#ccc",
                      }}
                    >
                      {COSTUME_META[data.avatar.costume]?.emoji || "🧍"}
                    </div>
                  </div>

                  {/* Hero name */}
                  <h3 className="font-headings uppercase text-xl leading-tight">
                    {data.avatar.name}
                  </h3>

                  {/* Costume label + hair label */}
                  <div className="flex gap-2 flex-wrap justify-center">
                    <span
                      className="cv-border-3 px-2 py-0.5 rounded-lg font-headings uppercase text-xs text-white"
                      style={{
                        background:
                          COSTUME_META[data.avatar.costume]?.color || "#888",
                      }}
                    >
                      {COSTUME_META[data.avatar.costume]?.label ||
                        data.avatar.costume}
                    </span>
                    {data.avatar.hair !== "bald" && (
                      <span
                        className="cv-border-3 px-2 py-0.5 rounded-lg font-headings uppercase text-xs text-white"
                        style={{
                          background: HAIR_COLORS[data.avatar.hair] || "#888",
                        }}
                      >
                        {data.avatar.hair.replace(/_/g, " ")}
                      </span>
                    )}
                  </div>

                  {/* Sidekick */}
                  {data.avatar.sidekick !== "none" && (
                    <div className="text-3xl" title={data.avatar.sidekick}>
                      {SIDEKICK_EMOJI[data.avatar.sidekick]}
                    </div>
                  )}

                  {/* Accessories */}
                  {data.avatar.accessories.length > 0 && (
                    <div className="flex flex-wrap gap-1 justify-center">
                      {data.avatar.accessories.map((a) => (
                        <span
                          key={a}
                          className="cv-border-3 bg-white px-2 py-0.5 rounded font-comic text-xs"
                          title={a}
                        >
                          {ACCESSORY_EMOJI[a] || a} {a}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h2 className="font-headings uppercase text-3xl mb-4">
                Style & font
              </h2>
              <Label className="font-headings uppercase mb-2 block">
                Art style
              </Label>
              <div className="grid sm:grid-cols-3 md:grid-cols-5 gap-3 mb-6">
                {ART_STYLES.map((s) => (
                  <button
                    key={s}
                    onClick={() => upd("art_style", s)}
                    className={`cv-border-3 rounded-xl p-4 text-center font-headings uppercase text-sm cv-press ${
                      data.art_style === s
                        ? "bg-purple-500 text-white cv-shadow"
                        : "bg-white cv-shadow-sm"
                    }`}
                    data-testid={`art-style-${s
                      .replace(/[^a-z]/gi, "-")
                      .toLowerCase()}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <Label className="font-headings uppercase mb-2 block">
                Font (preview in speech bubble)
              </Label>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                {FONTS.map((f) => (
                  <button
                    key={f}
                    onClick={() => upd("font", f)}
                    className={`cv-border-3 rounded-xl p-4 cv-press ${
                      data.font === f
                        ? "bg-yellow-300 cv-shadow"
                        : "bg-white cv-shadow-sm"
                    }`}
                  >
                    <div
                      className="speech-bubble"
                      style={{ fontFamily: f, fontSize: 22 }}
                    >
                      POW! {f}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <h2 className="font-headings uppercase text-3xl mb-6">Pages</h2>
              <div className="mb-8">
                <Label className="font-headings uppercase">
                  Page count:{" "}
                  <span className="text-purple-600">{data.page_count}</span>
                </Label>
                <Slider
                  min={4}
                  max={20}
                  step={1}
                  value={[data.page_count]}
                  onValueChange={(v) => upd("page_count", v[0])}
                  className="mt-3"
                  data-testid="page-slider"
                />
                <div className="flex justify-between font-body text-xs mt-1">
                  <span>4</span>
                  <span>20</span>
                </div>
              </div>
            </div>
          )}

          {step === 5 && (
            <div>
              <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <h2 className="font-headings uppercase text-3xl">
                  Script editor
                </h2>
                <Button
                  onClick={generateScript}
                  disabled={generatingScript}
                  className="bg-purple-600 hover:bg-purple-500 text-white cv-border cv-shadow-sm cv-press font-headings uppercase rounded-xl disabled:opacity-70"
                  data-testid="generate-script"
                >
                  {generatingScript ? (
                    <>
                      <Loader2 size={18} className="mr-1 animate-spin" />{" "}
                      Generating...
                    </>
                  ) : (
                    <>
                      <Wand2 size={18} className="mr-1" />{" "}
                      {data.panels.length ? "Regenerate" : "Generate with AI"}
                    </>
                  )}
                </Button>
              </div>
              {generatingScript ? (
                <div
                  className="cv-border-3 rounded-xl bg-yellow-100 p-12 text-center font-body font-bold flex flex-col items-center gap-3"
                  data-testid="script-generating"
                >
                  <Loader2 size={28} className="animate-spin text-purple-600" />
                  Writing your story with AI... ✨
                </div>
              ) : data.panels.length === 0 ? (
                <div className="cv-border-3 rounded-xl bg-yellow-100 p-12 text-center font-body font-bold">
                  Tap "Generate with AI" to write your story ✨
                </div>
              ) : (
                <div className="space-y-4 max-h-[480px] overflow-y-auto pr-2">
                  {data.panels.map((p, i) => (
                    <div
                      key={p.id || i}
                      className="cv-border-3 rounded-xl p-4 bg-white cv-shadow-sm"
                      data-testid={`panel-${i}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-headings uppercase">
                          Panel {i + 1}
                        </span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => rewritePanel(i)}
                            className="cv-border-3 bg-white p-1.5 rounded-lg cv-shadow-sm cv-press"
                            title="AI rewrite"
                          >
                            <RefreshCw size={14} />
                          </button>
                          <button
                            onClick={() => savePanel(i)}
                            className="cv-border-3 bg-yellow-300 px-3 py-1 rounded-lg cv-shadow-sm cv-press font-headings uppercase text-xs"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-3">
                        <div>
                          <Label className="font-comic text-xs">Scene</Label>
                          <Textarea
                            rows={2}
                            className="cv-border-3 rounded-lg font-body text-sm"
                            value={p.scene_description || ""}
                            onChange={(e) =>
                              editPanel(i, "scene_description", e.target.value)
                            }
                          />
                        </div>
                        <div>
                          <Label className="font-comic text-xs">
                            Narration (caption)
                          </Label>
                          <Textarea
                            rows={2}
                            className="cv-border-3 rounded-lg font-body text-sm"
                            value={p.narration || ""}
                            onChange={(e) =>
                              editPanel(i, "narration", e.target.value)
                            }
                          />
                        </div>
                        <div>
                          <Label className="font-comic text-xs">Dialogue</Label>
                          <Textarea
                            rows={2}
                            className="cv-border-3 rounded-lg font-body text-sm"
                            value={p.dialogue || ""}
                            onChange={(e) =>
                              editPanel(i, "dialogue", e.target.value)
                            }
                          />
                        </div>
                        <div>
                          <Label className="font-comic text-xs">SFX</Label>
                          <Input
                            className="cv-border-3 rounded-lg font-comic uppercase"
                            value={p.sfx || ""}
                            onChange={(e) =>
                              editPanel(i, "sfx", e.target.value)
                            }
                            placeholder="POW!"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 6 && (
            <div className="text-center py-12">
              <h2 className="font-headings uppercase text-4xl mb-4">
                Ready to make magic?
              </h2>
              <p className="font-body font-bold mb-8">
                We'll illustrate {data.panels.length} panels in {data.art_style}{" "}
                style.
              </p>
              <Button
                onClick={startGenerate}
                disabled={generating}
                className="bg-yellow-400 hover:bg-yellow-300 text-black cv-border cv-shadow cv-press font-headings text-2xl uppercase rounded-2xl py-8 px-12"
                data-testid="generate-comic"
              >
                <Sparkles className="mr-2" /> Make my comic!
              </Button>
              <p className="font-body text-sm mt-4 text-black/60">
                ~30-90 seconds. We'll redirect you to the reader.
              </p>
            </div>
          )}
        </div>

        {/* Nav */}
        <div className="flex justify-between mt-6">
          <Button
            onClick={prev}
            disabled={step === startStep}
            className="bg-purple-600 hover:bg-purple-500 text-white cv-border cv-shadow-sm cv-press font-headings uppercase rounded-xl"
            data-testid="wizard-prev"
          >
            <ChevronLeft /> Back
          </Button>
          {step < STEPS.length - 1 && (
            <Button
              onClick={next}
              disabled={!canNext()}
              className="bg-purple-600 hover:bg-purple-500 text-white cv-border cv-shadow-sm cv-press font-headings uppercase rounded-xl"
              data-testid="wizard-next"
            >
              Next <ChevronRight />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
